import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/ui/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import { bestSaving, type PaketHemat as Paket } from "@/lib/paket-hemat";
import { usePaketHemat } from "@/lib/use-paket-hemat";
import { cn } from "@/lib/utils";

/**
 * Every Paket Hemat on one page: what is in it, what it costs bought one by
 * one, and what the package costs instead.
 *
 * Rendered from the live catalogue rather than at build time, like the
 * catalogue page, so a package the admin builds after a deploy is here at
 * once, and the savings are always the ones checkout will honour. The CTA goes
 * to the product page rather than straight to the cart, where the customer
 * still chooses pasang and add-ons.
 */
export default function PaketHematPage() {
  const state = usePaketHemat({ fresh: true });
  const best = state.status === "ready" ? bestSaving(state.list) : 0;

  return (
    <main className="pt-28 pb-24">
      <Seo
        title="Paket Hemat — Kanata Indonesia"
        description="Meja dan aksesori Kanata dalam satu paket, dengan harga lebih hemat dibanding beli satuan."
        path="/paket-hemat"
      />

      <header className="mx-auto max-w-3xl px-5 text-center sm:px-8">
        <p className="inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.14em] text-sale">
          <span aria-hidden>✦</span>
          Paket Hemat
          <span aria-hidden>✦</span>
        </p>
        <h1 className="mt-4 text-4xl font-medium tracking-tight sm:text-5xl">
          Satu paket, ruang kerja beres.
        </h1>
        <p className="mt-5 text-lg leading-relaxed text-ink/65">
          Meja dan aksesori yang memang dipakai bersama, kami satukan dalam satu paket dengan harga
          lebih rendah daripada membelinya satu per satu.
          {best > 0 && (
            <>
              {" "}
              Hemat hingga <strong className="font-semibold text-sale">{formatIDR(best)}</strong>.
            </>
          )}
        </p>
      </header>

      <div className="mx-auto mt-16 max-w-6xl px-5 sm:px-8">
        {state.status === "loading" && <Loading />}
        {state.status === "error" && (
          <Notice role="alert">Paket Hemat gagal dimuat. Silakan muat ulang halaman.</Notice>
        )}
        {state.status === "ready" && state.list.length === 0 && (
          <Notice>
            Belum ada Paket Hemat saat ini.{" "}
            <Link to="/katalog" className="font-medium text-brand underline-offset-2 hover:underline">
              Lihat semua produk
            </Link>
          </Notice>
        )}
        {state.status === "ready" && state.list.length > 0 && (
          <ol className="space-y-20 sm:space-y-28">
            {state.list.map((paket, i) => (
              <li key={paket.product.id}>
                <PaketRow paket={paket} flip={i % 2 === 1} />
              </li>
            ))}
          </ol>
        )}
      </div>
    </main>
  );
}

function PaketRow({ paket, flip }: { paket: Paket; flip: boolean }) {
  const { product, sku, savings, image, soldOut } = paket;
  const items = sku.packageItems ?? [];
  const href = `/produk/${product.slug}`;

  return (
    <Reveal
      as="article"
      className="grid items-center gap-8 lg:grid-cols-[1.15fr_1fr] lg:gap-14"
    >
      <Link
        to={href}
        tabIndex={-1}
        aria-hidden
        className={cn(
          "group relative block overflow-hidden rounded-4xl bg-cream",
          flip && "lg:order-2",
        )}
      >
        <div className="aspect-[4/3]">
          {image ? (
            <img
              src={image}
              alt=""
              loading="lazy"
              className={cn(
                "size-full object-cover transition-transform duration-700 group-hover:scale-[1.03]",
                soldOut && "opacity-55",
              )}
            />
          ) : (
            <div className="size-full bg-brand-tint" />
          )}
        </div>
        {!soldOut && (
          <span className="absolute left-4 top-4 rounded-full bg-sale px-3 py-1 text-sm font-semibold text-white shadow-sm">
            Hemat {formatIDR(savings.amount)}
          </span>
        )}
      </Link>

      <div>
        {/* The saving is on the photo's pill; repeating it here said it twice. */}
        {soldOut && <Badge variant="soldOut">Stok habis</Badge>}
        <h2 className="mt-2 text-3xl font-medium tracking-tight sm:text-4xl">
          <Link to={href} className="hover:text-brand">
            {product.name}
          </Link>
        </h2>
        {product.tagline && <p className="mt-3 text-ink/65">{product.tagline}</p>}

        {items.length > 0 && (
          <div className="mt-6 rounded-2xl border border-ink/8 p-5">
            <h3 className="text-sm font-medium text-ink/55">Isi paket</h3>
            <ul className="mt-3 space-y-2.5">
              {items.map((item) => (
                <li key={item.skuId} className="flex items-baseline gap-3 text-sm">
                  <span className="w-7 shrink-0 tabular-nums text-ink/50">{item.qty}×</span>
                  {/* The SKU label already names the product and its colourway. */}
                  <span className="min-w-0 flex-1 text-ink">{item.sku?.label ?? "Produk"}</span>
                  {item.sku && (
                    <span className="shrink-0 tabular-nums text-ink/50">
                      {formatIDR(item.sku.priceIdr * item.qty)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <dl className="mt-6 flex flex-wrap items-end gap-x-6 gap-y-2">
          <div>
            <dt className="text-xs text-ink/50">Beli satuan</dt>
            <dd className="tabular-nums text-ink/50">
              <del>{formatIDR(sku.compareAtIdr ?? 0)}</del>
            </dd>
          </div>
          <div>
            <dt className="text-xs text-ink/50">Harga paket</dt>
            <dd className={cn("text-3xl font-medium tabular-nums", soldOut ? "text-ink/50" : "text-ink")}>
              {formatIDR(sku.priceIdr)}
            </dd>
          </div>
        </dl>

        <Button asChild size="lg" className="mt-7" variant={soldOut ? "outline" : "default"}>
          <Link to={href}>
            {soldOut ? "Lihat paket" : "Pilih paket ini"}
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </Reveal>
  );
}

function Loading() {
  return (
    <div role="status" aria-label="Memuat Paket Hemat" className="grid gap-8 lg:grid-cols-[1.15fr_1fr] lg:gap-14">
      <Skeleton className="aspect-[4/3] rounded-4xl" />
      <div className="space-y-4 self-center">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-3/4" />
        <Skeleton className="h-36 w-full rounded-2xl" />
        <Skeleton className="h-10 w-48" />
      </div>
    </div>
  );
}

function Notice({ children, role }: { children: ReactNode; role?: "alert" }) {
  return (
    <p role={role} className="rounded-2xl bg-cream px-6 py-10 text-center text-ink/70">
      {children}
    </p>
  );
}
